import { faArrowDown } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import useFetch from "./ApiClient/GetApi";
import { PostApi } from "./ApiClient/PostApi"; // ✅ needed for infinite load
import "./Category.css";
import VideoPlayer from './VideoPlayer/VideoPlayer';

const placeholderImg = "/no-image.png";
const CARDS_PER_VIEW = 3;

// ── helpers ─────────────────────────────────────────────────────────────
function thumb(n) { return n?.thumbnail || placeholderImg; }
function catName(n) { return n?.category?.name || "News"; }
// formatWhen -> "3 hours ago", "2 days ago", "in 5 minutes", "just now"
function formatWhen(dt, { locale = 'en' } = {}) {
  if (!dt) return '';
  const d = typeof dt === 'number' ? new Date(dt)
    : typeof dt === 'string' ? new Date(dt)
      : dt instanceof Date ? dt
        : null;
  if (!d || isNaN(d.getTime())) return '';

  const now = Date.now();
  let diff = d.getTime() - now; // future = positive, past = negative
  const abs = Math.abs(diff);

  // thresholds (ms)
  const sec = 1000;
  const min = 60 * sec;
  const hr = 60 * min;
  const day = 24 * hr;
  const wk = 7 * day;
  const mo = 30 * day;     // approx
  const yr = 365 * day;    // approx

  // "just now" small window
  if (abs < 5 * sec) return 'just now';

  // pick unit + value (rounded toward nearest)
  let value, unit;
  if (abs < min) { value = Math.round(diff / sec); unit = 'second'; }
  else if (abs < hr) { value = Math.round(diff / min); unit = 'minute'; }
  else if (abs < day) { value = Math.round(diff / hr); unit = 'hour'; }
  else if (abs < wk) { value = Math.round(diff / day); unit = 'day'; }
  else if (abs < mo) { value = Math.round(diff / wk); unit = 'week'; }
  else if (abs < yr) { value = Math.round(diff / mo); unit = 'month'; }
  else { value = Math.round(diff / yr); unit = 'year'; }

  // Prefer Intl.RelativeTimeFormat if available
  if (typeof Intl !== 'undefined' && Intl.RelativeTimeFormat) {
    const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
    return rtf.format(value, unit);
  }

  // Fallback: simple English strings
  const absVal = Math.abs(value);
  const plural = absVal === 1 ? '' : 's';
  return value < 0
    ? `${absVal} ${unit}${plural} ago`
    : `in ${absVal} ${unit}${plural}`;
}

const getThumb = (n) => n?.thumbnail || placeholderImg;
const getTagClass = (name = "News") => {
  const key = (name || "News").toLowerCase();
  if (["news"].includes(key)) return "news";
  if (["tech", "technology"].includes(key)) return "tech";
  if (["sports", "sport"].includes(key)) return "sports";
  if (["transport"].includes(key)) return "transport";
  if (["entertainment"].includes(key)) return "entertainment";
  if (["update", "updates"].includes(key)) return "update";
  return "lifestyle";
};

const truncate = (str, len = 200) =>
  (str || "").length > len ? (str || "").slice(0, len).trim() + "…" : (str || "");

// Trim rich HTML without breaking tags
// Remove HTML tags safely and truncate to N words
function plainSupport(html = "", maxWords = 200) {
  if (!html) return "";

  // 1) Use a detached DOM node so entities (e.g., &nbsp;) are decoded.
  const div = document.createElement("div");
  div.innerHTML = html;

  // 2) Drop non-text content
  div.querySelectorAll("script, style, noscript, iframe, svg").forEach(el => el.remove());

  // 3) Get plain text and normalize whitespace
  let text = (div.textContent || div.innerText || "").replace(/\s+/g, " ").trim();

  if (!text) return "";

  // 4) Truncate to N words
  const words = text.split(" ");
  if (words.length <= maxWords) return text;

  return words.slice(0, maxWords).join(" ") + "…";
}


export default function Category() {
  const location = useLocation();
  const categoryParam = location?.state?.category || "news";

  // ── initial page data ────────────────────────────────────────────────
  const { apiData } = useFetch(`/user/categoryHomePageData/${categoryParam}`);

  const { first, second, third, fourth } = useMemo(() => {
    const list = apiData?.data?.mainSection || [];
    const map = { first: null, second: null, third: null, fourth: null };
    list.forEach((row) => {
      if (!row?.key || !row?.news) return;
      map[row.key] = row.news;
    });
    return map;
  }, [apiData]);

  const [quickReadItems, setQuickReadItems] = useState([]);

  // Trending items
  const trendingItems = (apiData?.data?.trendingNews || [])
    .map((row) => row?.news)
    .filter(Boolean);

  // ── scroll indicator / carousel state ────────────────────────────────
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartX, setDragStartX] = useState(0);
  const [dragStartSlide, setDragStartSlide] = useState(0);
  const scrollIndicatorRef = useRef(null);

  const bannerAdRef = useRef(null);
  const bannerTextRef = useRef(null);
  const bannerBtnRef = useRef(null);

  const lunaticBannerRef = useRef(null);
  const lunaticOverlayRef = useRef(null);
  const lunaticLogoRef = useRef(null);
  const lunaticTitleRef = useRef(null);
  const lunaticDescRef = useRef(null);
  const lunaticBtnRef = useRef(null);

  const [ads, setAds] = useState([]);
  const INDICATOR_START = 8.5;   // %
  const INDICATOR_END = 91.5;    // %
  console.log(apiData)
  // ── quick read & ads from initial payload ────────────────────────────
  useEffect(() => {
    if (apiData?.data?.quickRead) {
      const items = apiData.data.quickRead.map((dd) => ({
        img: dd.thumbnail ?? "/20.jpg",
        tag: dd?.category?.name ?? "TECH",
        tagClass: (dd?.category?.name ?? "tech").toLowerCase(),
        headline: dd.title ?? "A Surprising New Pokémon Game Is Free To Play From Your Smartphone",
        support: dd.content ?? "Gotta search 'em all!",
        slug: dd.slug ?? "Gotta search 'em all!"
      }));
      setQuickReadItems(items);
    }
  }, [apiData]);

  useEffect(() => {
    if (apiData?.data?.addData) {
      setAds(apiData.data.addData);
    }
  }, [apiData]);

  // Memoized chunks/pages/maxSlide for trending carousel
  const { chunks, pages, maxSlide } = useMemo(() => {
    const _chunks = [];
    for (let i = 0; i < trendingItems.length; i += CARDS_PER_VIEW) {
      _chunks.push(trendingItems.slice(i, i + CARDS_PER_VIEW));
    }
    const _pages = Math.max(1, _chunks.length);
    return { chunks: _chunks, pages: _pages, maxSlide: _pages - 1 };
  }, [trendingItems]);

  // Keep slide index in range if data changes
  useEffect(() => {
    if (currentSlide > maxSlide) setCurrentSlide(maxSlide);
  }, [maxSlide, currentSlide]);

  const goToSlide = (i) => {
    if (i < 0) i = 0;
    if (i > maxSlide) i = maxSlide;
    setCurrentSlide(i);
  };

  // Drag handlers (indicator)
  const handleDragStart = (e) => {
    e.preventDefault();
    setIsDragging(true);
    setDragStartX(e.type === 'mousedown' ? e.clientX : e.touches[0].clientX);
    setDragStartSlide(currentSlide);
  };

  const handleDragMove = (e) => {
    if (!isDragging) return;
    e.preventDefault();

    const currentX = e.type === 'mousemove' ? e.clientX : e.touches[0].clientX;
    const deltaX = currentX - dragStartX;

    const trackEl = scrollIndicatorRef.current;
    const containerWidth = trackEl ? trackEl.offsetWidth : window.innerWidth;

    const effectiveWidthPx = containerWidth * ((INDICATOR_END - INDICATOR_START) / 100);
    const stepPx = maxSlide > 0 ? (effectiveWidthPx / maxSlide) : effectiveWidthPx;

    const slideChange = Math.round(deltaX / stepPx);
    const next = Math.max(0, Math.min(maxSlide, dragStartSlide + slideChange));
    setCurrentSlide(next);
  };

  const onLineClick = (e) => {
    const trackEl = scrollIndicatorRef.current;
    if (!trackEl) return;

    const rect = trackEl.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const containerWidth = rect.width;

    const startPx = containerWidth * (INDICATOR_START / 100);
    const endPx = containerWidth * (INDICATOR_END / 100);
    const clamped = Math.max(startPx, Math.min(endPx, x));
    const t = (clamped - startPx) / Math.max(1, (endPx - startPx));
    const target = maxSlide > 0 ? Math.round(t * maxSlide) : 0;

    goToSlide(target);
  };

  const handleDragEnd = () => setIsDragging(false);

  useEffect(() => {
    const handleGlobalMouseMove = (e) => handleDragMove(e);
    const handleGlobalMouseUp = () => handleDragEnd();
    const handleGlobalTouchMove = (e) => handleDragMove(e);
    const handleGlobalTouchEnd = () => handleDragEnd();

    if (isDragging) {
      document.addEventListener('mousemove', handleGlobalMouseMove);
      document.addEventListener('mouseup', handleGlobalMouseUp);
      document.addEventListener('touchmove', handleGlobalTouchMove, { passive: false });
      document.addEventListener('touchend', handleGlobalTouchEnd);
    }

    return () => {
      document.removeEventListener('mousemove', handleGlobalMouseMove);
      document.removeEventListener('mouseup', handleGlobalMouseUp);
      document.removeEventListener('touchmove', handleGlobalTouchMove);
      document.removeEventListener('touchend', handleGlobalTouchEnd);
    };
  }, [isDragging, dragStartX, dragStartSlide]);

  const categoryLabel = catName(first) || catName(second) || catName(third) || catName(fourth);
  const latestNews = apiData?.data?.latestNews || [];
  const [loading, setLoading] = useState(false);

  // indicator positions
  const t = maxSlide > 0 ? currentSlide / maxSlide : 0;
  const indicatorLeftPct = INDICATOR_START + t * (INDICATOR_END - INDICATOR_START);
  const dotLeftPct = (i) => {
    const tt = maxSlide > 0 ? i / maxSlide : 0;
    return INDICATOR_START + tt * (INDICATOR_END - INDICATOR_START);
  };

  // ====================================================================
  // INFINITE SCROLL: "More in this category"
  // ====================================================================

  // Feed state
  const [moreNews, setMoreNews] = useState([]);
  const [page, setPage] = useState(1); // change to 0 if your API is 0-based
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const loaderRef = useRef(null);
  const observerRef = useRef(null);

  // Reset paging when category changes
  useEffect(() => {
    setMoreNews([]);
    setPage(1);
    setHasMore(true);
    setLoadingMore(false);
  }, [categoryParam]);

  // Helpers (use window; set a root if you scroll inside a container)
  const getRootEl = () => null;
  const isInView = (el, rootEl = null, extra = 0) => {
    if (!el) return false;
    const rect = el.getBoundingClientRect();
    let viewTop = 0, viewBottom = window.innerHeight;
    if (rootEl) {
      const r = rootEl.getBoundingClientRect();
      viewTop = r.top;
      viewBottom = r.bottom;
    }
    return rect.top < viewBottom + extra && rect.bottom > viewTop - extra;
  };

  // IDs to exclude (already shown)
  const excludeIds = useMemo(() => {
    const ids = new Set();
    [first, second, third, fourth].forEach(n => n?.id && ids.add(n.id));
    trendingItems.forEach(n => n?.id && ids.add(n.id));
    moreNews.forEach(n => n?.id && ids.add(n.id));
    return Array.from(ids);
  }, [first, second, third, fourth, trendingItems, moreNews]);
  function toQuickItem(row) {
    const tag = pickTag(row);
    return {
      id: row.id,
      img: row.thumbnail || row.cover || "/20.jpg",
      tag,
      tagClass: tagClassFor(tag),
      headline: row.title || row.seo_title || "Untitled",
      support: plainSupport(row.seo_description || row.summary || row.content || "", 200),
      to: `/article/${encodeURIComponent(row.slug || row.id)}`
    };
  }

  function pickTag(row) {
    const fromCat = row?.category?.name;
    if (fromCat) return fromCat;
    const firstTag = (row?.tags || "").split(",")[0]?.trim();
    return firstTag || "News";
  }
  function tagClassFor(name = "News") {
    const key = (name || "News").toLowerCase();
    if (["news"].includes(key)) return "news";
    if (["tech", "technology"].includes(key)) return "tech";
    if (["sports", "sport"].includes(key)) return "sports";
    if (["transport"].includes(key)) return "transport";
    if (["entertainment"].includes(key)) return "entertainment";
    if (["update", "updates", "review"].includes(key)) return "update";
    return "lifestyle";
  }
  // Fetch more items (POST)
  const fetchMore = useCallback(async () => {
    if (loadingMore || !hasMore) return;

    setLoadingMore(true);
    try {
      const payload = {
        category: categoryParam,
        page,
        limit: 6,
        excludeIds,
      };

      const res = await PostApi("/user/getCategoryMoreNews", payload);

      const ok = res?.data?.status === 1 || res?.data?.status === "1";
      const data = res?.data?.data || {};
      const items = Array.isArray(data.items) ? data.items : Array.isArray(data) ? data : [];
      const nextHasMore = typeof data.hasMore === "boolean" ? data.hasMore : (items.length > 0);

      // de-dupe for the "More in category" grid
      setMoreNews((prev) => {
        const seen = new Set(prev.map((n) => n.id));
        excludeIds.forEach(id => seen.add(id));
        const incoming = items.filter((n) => n?.id && !seen.has(n.id));
        return incoming.length ? [...prev, ...incoming] : prev;
      });

      // ✅ ALSO append to Quick Read (de-duped by id)
      setQuickReadItems((prev) => {
        const seen = new Set(prev.map(q => q.id));
        const incomingQuick = items
          .filter(n => n?.id && !seen.has(n.id))
          .map(toQuickItem);
        return incomingQuick.length ? [...prev, ...incomingQuick] : prev;
      });

      setHasMore(nextHasMore);
      if (ok && nextHasMore) setPage((p) => p + 1);
    } catch (err) {
      console.error("Category.fetchMore error:", err);
    } finally {
      setLoadingMore(false);
    }
  }, [page, hasMore, loadingMore, categoryParam, excludeIds]);


  // Build IntersectionObserver AFTER initial payload is ready (so excludeIds is correct)
  useEffect(() => {
    if (!loaderRef.current) return;

    if (observerRef.current) observerRef.current.disconnect();

    const rootEl = getRootEl();
    observerRef.current = new IntersectionObserver(
      (entries) => {
        const first = entries[0];
        if (first.isIntersecting && hasMore && !loadingMore) {
          fetchMore();
        }
      },
      {
        root: rootEl || null,
        threshold: 0.01,
        rootMargin: "800px 0px",
      }
    );

    observerRef.current.observe(loaderRef.current);
    return () => observerRef.current?.disconnect();
  }, [fetchMore, hasMore, loadingMore, categoryParam]);

  // Kickstart if page is short or sentinel already visible
  useEffect(() => {
    if (!hasMore || loadingMore) return;

    const rootEl = getRootEl();
    const el = loaderRef.current;
    const docShort = rootEl
      ? rootEl.scrollHeight <= rootEl.clientHeight + 10
      : document.body.scrollHeight <= window.innerHeight + 10;

    if (docShort || isInView(el, rootEl, 900)) {
      fetchMore();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryParam]); // run when category changes

  // Fallback scroll listener (optional)
  useEffect(() => {
    if (!hasMore) return;
    const rootEl = getRootEl();
    const target = rootEl || window;

    const onScroll = () => {
      if (loadingMore) return;
      if (isInView(loaderRef.current, rootEl, 900)) {
        fetchMore();
      }
    };

    target.addEventListener("scroll", onScroll, { passive: true });
    return () => target.removeEventListener("scroll", onScroll);
  }, [hasMore, loadingMore, fetchMore]);

  // Renderer for a single appended item
  // uses the same quickread card UI for "more news"
  const MoreNewsCard = ({ item }) => {
    // convert raw API row -> quick card fields (shorten support to 40 words)
    const qi = toQuickItem(item);                    // uses your helper we added earlier
    const support40 = plainSupport(
      item.seo_description || item.summary || item.content || qi.support || "",
      40
    );

    return (
      <div className="quickread-card exact-quickread-card">
        <img
          className="quickread-img exact-quickread-img"
          src={qi.img}
          alt={qi.headline}
          style={{
            width: '300px',
            height: '200px',
            objectFit: 'cover',
            minWidth: '300px',
            minHeight: '200px'
          }}
        />
        <div className="quickread-content exact-quickread-content">
          <span className={`tag ${qi.tagClass} exact-quickread-tag`}>{qi.tag}</span>
          <div className="exact-quickread-body">
            <Link
              to={`/article/${item?.slug}`}
              style={{ textDecoration: 'none', color: 'inherit' }}
            >
              <div className="quickread-headline exact-quickread-headline">
                {qi.headline}
              </div>
            </Link>
            <div className="exact-quickread-support">
              {support40}
            </div>
          </div>
        </div>
      </div>
    );
  };


  // ── loading for initial payload ───────────────────────────────────────
  if (!apiData) {
    return (
      <div style={{ height: "100vh" }}>
        <div className="quickread-loader"><div className="spinner" /></div>
      </div>
    );
  }

  return (
    <>
      <div className="home-container">

        <main>
          <VideoPlayer url="https://www.youtube.com/watch?v=3VpFFKGCaLQ&list=RD3VpFFKGCaLQ&start_radio=1" />
          {/* NEWS HEADING */}
          <div className="news-heading">
            <h1>News</h1>
            <div className="news-heading-border"></div>
          </div>

          {/* HERO SECTION */}
          <section className="hero-section-category custom-hero-animate">
            {/* ===== SIDEBAR ===== */}
            <aside className="hero-sidebar custom-slide-in">
              {/* Sidebar Featured: FOURTH */}
              {fourth && (
                <div className="sidebar-featured-article">
                  <img src={thumb(fourth)} alt={fourth.title} />
                  <div className="sidebar-featured-content">
                    <span className="tag news">{catName(fourth)}</span>
                    <Link
                      to={`/article/${fourth.slug}`}
                      style={{ textDecoration: "none", color: "inherit" }}
                    >
                      <h3>{fourth.title}</h3>
                    </Link>
                  </div>
                </div>
              )}

              <div className="sidebar-title">
                LATEST NEWS{" "}
                <span className="sidebar-plus">
                  <FontAwesomeIcon icon={faArrowDown} />
                </span>
              </div>

              <ul className="sidebar-category-list">
                {(apiData?.data?.latestNews || []).length === 0 ? (
                  <li className="text-muted">No latest news</li>
                ) : (
                  (apiData?.data?.latestNews || []).map((n) => (
                    <li key={n.id}>
                      <Link
                        to={`/article/${n?.slug}`}
                        style={{ textDecoration: "none", color: "inherit" }}
                      >
                        <b>{n.title?.length > 70 ? n.title.slice(0, 70) + "..." : n.title}</b>
                      </Link>
                      <div className="sidebar-list-desc">
                        {formatWhen(n.createdAt)}
                        {n.user?.name ? <> | {n.user.name}</> : null}
                      </div>
                    </li>
                  ))
                )}
              </ul>
            </aside>

            {/* ===== MAIN ===== */}
            <div className="hero-main">
              {/* Big Featured: FIRST */}
              {first && (
                <div className="featured-article custom-fade-in-left">
                  <img src={thumb(first)} alt={first.title} />
                  <div className="featured-content custom-fade-in">
                    <span className="tag news">{categoryLabel}</span>
                    <Link
                      to={`/article/${first.slug}`}
                      style={{ textDecoration: "none", color: "inherit" }}
                    >
                      <h2>{first.title}</h2>
                    </Link>
                  </div>
                </div>
              )}

              {/* Mini cards: SECOND + THIRD */}
              <div className="mini-news-row custom-fade-in-right">
                <div className="mini-news-card">
                  {second ? (
                    <>
                      <img src={thumb(second)} alt={second.title} />
                      <div className="mini-news-content">
                        <span className="tag news">{catName(second)}</span>
                        <Link
                          to={`/article/${second.slug}`}
                          style={{ textDecoration: "none", color: "inherit" }}
                        >
                          <p>{second.title}</p>
                        </Link>
                      </div>
                    </>
                  ) : (
                    <div className="text-muted small p-3">No item</div>
                  )}
                </div>

                <div className="mini-news-card">
                  {third ? (
                    <>
                      <img src={thumb(third)} alt={third.title} />
                      <div className="mini-news-content">
                        <span className="tag update">{catName(third)}</span>
                        <Link
                          to={`/article/${third.slug}`}
                          style={{ textDecoration: "none", color: "inherit" }}
                        >
                          <p>{third.title}</p>
                        </Link>
                      </div>
                    </>
                  ) : (
                    <div className="text-muted small p-3">No item</div>
                  )}
                </div>
              </div>
            </div>
          </section>

          {/* Lunatic Fringe Banner */}
          {ads[0] && (
            <div>
              <div
                ref={lunaticBannerRef}
                className="lunatic-banner"
                style={{ backgroundImage: `url(${ads[0].image})` }}
              >
                <div ref={lunaticOverlayRef} className="lunatic-banner-overlay lunatic-banner-row">
                  <div className="lunatic-banner-content-col">
                    <div ref={lunaticLogoRef} className="lunatic-logo">If</div>
                    <div ref={lunaticTitleRef} className="lunatic-title">{ads[0].title}</div>
                    <div ref={lunaticDescRef} className="lunatic-desc">{ads[0]?.description}</div>
                  </div>
                  <button
                    onClick={() => window.open(ads[0].link ? ads[0].link : "#", "_blank")}
                    ref={lunaticBtnRef}
                    className="lunatic-btn"
                  >
                    BOOK NOW
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* TRENDING NEWS SECTION */}
          <section className="section-block trending-news-section">
            <div className="section-title trending">TRENDING NEWS</div>

            <div className="trending-carousel-container">
              {/* Track & slides */}
              <div
                className="trending-carousel"
                style={{
                  width: `${pages * 100}%`,
                  transform: `translateX(-${(currentSlide * 100) / pages}%)`,
                  transition: 'transform 400ms ease',
                  display: 'flex',
                }}
              >
                {chunks.map((pageItems, idx) => (
                  <div
                    key={idx}
                    className="trending-page"
                    style={{ flex: `0 0 ${100 / pages}%`, display: 'flex', gap: '16px' }}
                  >
                    {pageItems.map((n) => (
                      <div key={n.id} className="trending-carousel-card">
                        <div className="trending-card-bg">
                          <img src={getThumb(n)} alt={n?.title || "Trending"} />
                          <div className="card-overlay"></div>
                        </div>
                        <div
                          className="trending-card-content"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <span className={`tag ${getTagClass(n?.category?.name || "News")}`}>
                            {(n?.category?.name || "News").toUpperCase()}
                          </span>
                          <Link
                            to={`/article/${n.slug}`}
                            style={{ textDecoration: 'none', color: 'inherit' }}
                          >
                            <h3>{n?.title || "Untitled"}</h3>
                          </Link>
                        </div>
                      </div>
                    ))}

                    {/* pad last page to 3 so layout doesn’t shift */}
                    {pageItems.length < CARDS_PER_VIEW &&
                      Array.from({ length: CARDS_PER_VIEW - pageItems.length }).map((_, i) => (
                        <div
                          key={`empty-${i}`}
                          className="trending-carousel-card"
                          style={{ visibility: 'hidden' }}
                        />
                      ))}
                  </div>
                ))}
              </div>

              {/* Scroll Indicator */}
              <div className="trending-scroll-indicator" ref={scrollIndicatorRef}>
                <div className="scroll-line" onClick={onLineClick} />

                <div
                  className={`scroll-indicator ${isDragging ? 'dragging' : ''}`}
                  style={{
                    left: `${indicatorLeftPct}%`,        // 8.5 … 91.5
                    transform: 'translateX(-50%)',
                    cursor: isDragging ? 'grabbing' : 'grab',
                    position: 'absolute',
                  }}
                  onMouseDown={handleDragStart}
                  onTouchStart={handleDragStart}
                />

                <div className="scroll-dots">
                  {Array.from({ length: pages }).map((_, i) => (
                    <div
                      key={i}
                      className={`scroll-dot ${currentSlide === i ? 'active' : ''}`}
                      onClick={() => goToSlide(i)}
                      style={{
                        left: `${dotLeftPct(i)}%`,        // aligned to 8.5 … 91.5
                        transform: 'translateX(-50%)',
                        position: 'absolute',
                      }}
                    />
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Banner Ad */}
          {ads[1] && (
            <div>
              <div
                ref={bannerAdRef}
                className="banner-ad"
                style={{ backgroundImage: `url(${ads[1].image})` }}
              >
                <span ref={bannerTextRef} className="banner-ad-text">{ads[1].title}</span>
                <span ref={bannerBtnRef} className="ad-btn">SHOP NOW</span>
              </div>
            </div>
          )}

          {/* QUICK READ SECTION */}
          <section className="quickread-section exact-quickread">
            <div className="section-title quickread">QUICK READ</div>
            <div className="quickread-list exact-quickread-list">
              {quickReadItems.map((item, idx) => {
                const elements = [];
                elements.push(
                  <div key={idx} className="quickread-card exact-quickread-card">
                    <img
                      className="quickread-img exact-quickread-img"
                      src={item.img}
                      alt={item.headline}
                      style={{
                        width: '300px',
                        height: '200px',
                        objectFit: 'cover',
                        minWidth: '300px',
                        minHeight: '200px'
                      }}
                    />
                    <div className="quickread-content exact-quickread-content">
                      <span className={`tag ${item.tagClass} exact-quickread-tag`}>{item.tag}</span>
                      <div className="exact-quickread-body">
                        <Link to={`/article/${item.slug}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                          <div className="quickread-headline exact-quickread-headline">{item.headline}</div>
                        </Link>
                        <div className="exact-quickread-support">
                          {plainSupport(item.support, 40)}
                        </div>

                      </div>
                    </div>
                  </div>
                );
                if ((idx + 1) % 4 === 0) {
                  const bannerGroup = Math.floor((idx + 1) / 4);
                  const isNewBanner = bannerGroup % 2 === 1;
                  elements.push(
                    <div key={`banner-${idx}`} className="quickread-banner-wrapper">
                      {isNewBanner ? (
                        <div className="banner-ad-1" style={{ backgroundImage: "url('/image.png')" }}>
                          <span className="banner-ad-text">Bramleys.</span>
                          <span className="ad-btn">SHOP NOW</span>
                        </div>
                      ) : (
                        <div className="banner-ad" style={{ backgroundImage: "url('/4.jpg')" }}>
                          <span className="banner-ad-text">Bramleys.</span>
                          <span className="ad-btn">SHOP NOW</span>
                        </div>
                      )}
                    </div>
                  );
                }
                return elements;
              })}
            </div>
          </section>

          {/* =========================================================================
              MORE IN THIS CATEGORY (Infinite)
          ========================================================================= */}
          <section className="more-in-category">


            <div className="more-news-grid">
              {moreNews.map((item) => (
                <MoreNewsCard key={item?.id || item?.slug || item?.title} item={item} />
              ))}
            </div>

            {/* Sentinel / Loader */}
            <div ref={loaderRef} style={{ textAlign: "center", margin: "2rem 0", minHeight: 60 }}>
              {loadingMore && (
                <div
                  className="spinner"
                  style={{
                    width: 38,
                    height: 38,
                    border: "4px solid #c20a7c",
                    borderTop: "4px solid #fff",
                    borderRadius: "50%",
                    animation: "spin 1s linear infinite",
                    margin: "0 auto",
                  }}
                />
              )}
              {!hasMore && (
                <div style={{ opacity: 0.6, fontSize: 14, padding: 8 }}>
                  You’re all caught up.
                </div>
              )}
            </div>
          </section>
        </main>
      </div>
    </>
  );
}
